// Dependencias.
import Vue from 'vue';

// Estado inicial
const initialState = () => ({
  settings: [],
  total: 0,
  paginaAtual: 1,
  totalPorPagina: 20,
  loadingState: false,
});

// Objeto de estado
const state = initialState();

// Getters
const getters = {
  getSettingsList(state) {
    return state.settings;
  },
  getTotal(state) {
    return state.total;
  },
  getLoadingState(state) {
    return state.loadingState;
  },
};

// Ações
const actions = {
  clearState({ commit }) {
    commit('RESET');
  },

  createSettings({ commit }, { application, customer, name, entrances }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes`;
      commit('setLoadingState', true);
      const response = await api.send(
        url,
        'POST',
        {
          aplicacao: application,
          cliente: customer,
          entradas: entrances.map(entrada => ({
            ...entrada,
            chave: String(entrada.chave || ''),
            valor: String(entrada.valor || '')
          })),
          nome: name,
        },
        undefined,
        false
      );
      commit('setLoadingState', false);
      if ([200, 201].includes(response.status)) {
        resolve({
          status: 200,
          msg: 'Configuração criada com sucesso!',
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  async fetchSettings({ commit }, { filter, pagina, totalPorPagina }) {
    commit('setLoadingState', true);
    const api = new Vue.prototype.$httpClient();
    const url = `/configuracoes/listar`;
    const response = await api.send(
      url,
      'POST',
      {
        filtro: filter ? filter : undefined,
        pagina,
        totalPorPagina
      }
    );
    commit('setLoadingState', false);

    let lista = [];
    let total = 0;
    let paginaAtual = 1;
    let totalPorPaginaValue = 20;

    if (Array.isArray(response.data.dados)) {
      lista = response.data.dados;
    }
    if (response.data.paginacao) {
      total = response.data.paginacao.total;
      paginaAtual = response.data.paginacao.paginaAtual;
      totalPorPaginaValue = response.data.paginacao.totalPorPagina;
    } else {
      total = lista.length;
      paginaAtual = 1;
      totalPorPaginaValue = 20;
    }

    commit('setSettings', lista);
    commit('setTotal', total);
    commit('setPaginaAtual', paginaAtual);
    commit('setTotalPorPagina', totalPorPaginaValue);

    // Retornar informações da resposta
    return {
      status: response.status,
      statusCode: response.status,
      mensagem: response.data.mensagem || 'Lista de configurações recuperada com sucesso',
      dados: lista
    };
  },

  async fetchSettingsForComparison({ commit }, { filter, pagina, totalPorPagina }) {
    const api = new Vue.prototype.$httpClient();
    const url = `/configuracoes/listar`;
    const response = await api.send(
      url,
      'POST',
      {
        filtro: filter ? filter : undefined,
        pagina,
        totalPorPagina
      }
    );

    let lista = [];

    if (Array.isArray(response.data.dados)) {
      lista = response.data.dados;
    }

    // Retornar informações da resposta sem afetar o estado global
    return {
      status: response.status,
      statusCode: response.status,
      mensagem: response.data.mensagem || 'Lista de configurações recuperada com sucesso',
      dados: lista
    };
  },

  fetchSettingById({ commit }, { id }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes/${id}`;

      commit('setLoadingState', true);

      const response = await api.send(url, 'GET', null, undefined, false);

      commit('setLoadingState', false);

      if (response.status == 200) {
        resolve({
          status: 200,
          msg: 'Dados da configuração foram recuperados com sucesso.',
          dados: {
            ...response.data.dados,
          },
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  fetchOtherClientsSettings({ commit }, { filter }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();

      const url = `/configuracoes/listar`;

      commit('setLoadingState', true);

      const response = await api.send(
        url,
        'POST',
        {
          filtro: filter,
        },
        undefined,
        false
      );

      commit('setLoadingState', false);

      if (response.status == 200) {
        resolve({
          status: 200,
          msg: 'Lista de configurações recuperada.',
          dados: response.data.dados,
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  removeSettings({ commit }, { id }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes/${id}`;
      commit('setLoadingState', true);
      const response = await api.send(url, 'DELETE');
      commit('setLoadingState', false);
      if ([200, 201].includes(response.status)) {
        resolve({
          status: 200,
          msg: `Configuração de id ${id} removida com sucesso!`,
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  updateEntrances({ commit }, { id, entrances }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes`;
      const response = await api.send(
        url,
        'PUT',
        {
          id: id,
          entradas: entrances.map(entrada => ({
            ...entrada,
            chave: String(entrada.chave || ''),
            valor: String(entrada.valor || '')
          })),
        },
        undefined,
        false
      );
      if ([200, 201].includes(response.status)) {
        resolve({
          status: 200,
          msg: 'Entradas atualizadas com sucesso!',
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  // Nova action para salvamento silencioso (sem afetar loading global)
  updateEntrancesSilent({ commit }, { id, entrances }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes`;
      // Não seta loading global
      const response = await api.send(
        url,
        'PUT',
        {
          id: id,
          entradas: entrances.map(entrada => ({
            ...entrada,
            chave: String(entrada.chave || ''),
            valor: String(entrada.valor || '')
          })),
        },
        undefined,
        false
      );
      if ([200, 201].includes(response.status)) {
        resolve({
          status: 200,
          msg: 'Entradas atualizadas com sucesso!',
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  /**
   * Action para atualização atômica de entradas específicas (usa PATCH)
   * Envia apenas as entradas modificadas, evitando conflitos de edição concorrente
   * 
   * @param {string} id - UUID da configuração
   * @param {Array} modifiedEntrances - Array com apenas as entradas modificadas
   * @param {string} tokenJWT2FA - Token JWT 2FA para operações críticas
   * @returns {Promise} Resultado da operação
   */
  updateModifiedEntrances({ commit }, { id, modifiedEntrances, tokenJWT2FA }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes/entradas`;
      commit('setLoadingState', true);
      
      const response = await api.send(
        url,
        'PATCH',
        {
          id: id,
          entradas: modifiedEntrances.map(entrada => ({
            chave: String(entrada.chave || ''),
            valor: entrada.valor // Mantém o tipo original do valor
          })),
          tokenJWT2FA: tokenJWT2FA || null
        },
        undefined,
        false
      );
      
      commit('setLoadingState', false);
      
      if ([200, 201].includes(response.status)) {
        resolve({
          status: 200,
          msg: response.data.mensagem || 'Entradas atualizadas com sucesso!',
          dados: response.data.dados
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  /**
   * Action silenciosa para atualização atômica (usa PATCH, sem loading global)
   * Ideal para auto-save e salvamentos em background
   * 
   * @param {string} id - UUID da configuração
   * @param {Array} modifiedEntrances - Array com apenas as entradas modificadas
   * @returns {Promise} Resultado da operação
   */
  updateModifiedEntrancesSilent({ commit }, { id, modifiedEntrances }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes/entradas`;
      // Não seta loading global para não interferir na UX
      
      const response = await api.send(
        url,
        'PATCH',
        {
          id: id,
          entradas: modifiedEntrances.map(entrada => ({
            chave: String(entrada.chave || ''),
            valor: entrada.valor // Mantém o tipo original do valor
          })),
        },
        undefined,
        false
      );
      
      if ([200, 201].includes(response.status)) {
        resolve({
          status: 200,
          msg: response.data.mensagem || 'Entradas atualizadas com sucesso!',
          dados: response.data.dados
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  alterarTodasEntradas({ commit }, { nome, aplicacao, chave, valor }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes/alterarTodosClientes`;
      commit('setLoadingState', true);
      const response = await api.send(
        url,
        'PUT',
        {
         nome, aplicacao, chave, valor: String(valor)
        },
        undefined,
        false
      );
      commit('setLoadingState', false);
      if ([200, 201].includes(response.status)) {
        resolve({
          status: 200,
          msg: 'Todas as entradas foram alteradas!',
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  clonarConfiguracoes({ commit }, { application, customer, newCustomer }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes/clonar`;
      commit('setLoadingState', true);
      const response = await api.send(url, 'POST', {
        aplicacao: application,
        cliente: customer,
        novoCliente: newCustomer.toUpperCase(),
      });
      commit('setLoadingState', false);
      if (response.status == 201) {
        resolve({
          status: 200,
          msg: response.data.mensagem,
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  async fetchSettingByClienteENome({ commit }, { cliente, nome, listaStatus }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes/listar`;
      commit('setLoadingState', true);
      
      // Preparar filtro com status se fornecido
      const filtro = {
        cliente: String(cliente),
        nome: String(nome)
      };
      
      // Adicionar filtro de status se fornecido
      if (listaStatus && Array.isArray(listaStatus)) {
        filtro.listaStatus = listaStatus;
      }
      
      const response = await api.send(
        url,
        'POST',
        {
          filtro: filtro
        },
        undefined,
        false
      );
      commit('setLoadingState', false);
      if (response.status == 200 && response.data.dados && response.data.dados.length > 0) {
        resolve({
          status: 200,
          msg: 'Configuração recuperada com sucesso.',
          dados: response.data.dados[0],
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Configuração não encontrada.',
        });
      }
    });
  },

  // Aplicar valor único em todas as configurações
  updateSettingsValorUnico({ commit }, { aplicacao, nome, chave, valor }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes/valorUnico`;
      commit('setLoadingState', true);

      const response = await api.send(
        url,
        'PUT',
        {
          aplicacao,
          nome,
          chave,
          valor: String(valor)
        },
        undefined,
        false
      );

      commit('setLoadingState', false);

      if ([200, 201].includes(response.status)) {
        resolve({
          status: 200,
          msg: response.data.mensagem || 'Valor único aplicado com sucesso!',
          dados: response.data.dados
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  // Aplicar alterações em massa (individuais por cliente)
  updateSettingsEmMassa({ commit }, { aplicacao, nome, chave, alteracoes }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes/emMassa`;
      commit('setLoadingState', true);

      const response = await api.send(
        url,
        'PUT',
        {
          aplicacao,
          nome,
          chave,
          alteracoes: alteracoes.map(alt => ({
            ...alt,
            cliente: String(alt.cliente),
            valor: String(alt.valor)
          }))
        },
        undefined,
        false
      );

      commit('setLoadingState', false);

      if ([200, 201, 207].includes(response.status)) {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Alterações em massa aplicadas com sucesso!',
          dados: response.data.dados
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  // Nova action que usa a rota mais descritiva - decide automaticamente qual endpoint usar
  editarConfiguracoesEmMassa({ commit }, { aplicacao, nome, chave, valor, alteracoes }) {
    return new Promise(async resolve => {
      const api = new Vue.prototype.$httpClient();
      const url = `/configuracoes/editarEmMassa/${nome}/${chave}`;
      commit('setLoadingState', true);

      // Preparar payload baseado no tipo de operação
      let payload = {};
      
      // Só incluir aplicacao no payload se foi fornecida (senão usa padrão do backend)
      if (aplicacao) {
        payload.aplicacao = aplicacao;
      }
      
      if (valor !== undefined && !alteracoes) {
        // Valor único
        payload.valor = String(valor);
      } else if (alteracoes && Array.isArray(alteracoes)) {
        // Array de alterações - converter valores para string
        payload.alteracoes = alteracoes.map(alt => ({
          ...alt,
          cliente: String(alt.cliente),
          valor: String(alt.valor)
        }));
      }

      const response = await api.send(
        url,
        'PUT',
        payload,
        undefined,
        false
      );

      commit('setLoadingState', false);

      if ([200, 201, 207].includes(response.status)) {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Configurações editadas com sucesso!',
          dados: response.data.dados
        });
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecado durante sua requisição.',
        });
      }
    });
  },
};

// Mutações
const mutations = {
  RESET(state) {
    const newState = initialState();
    Object.keys(newState).forEach(key => {
      state[key] = newState[key];
    });
  },

  setSettings(state, lista) {
    state.settings = lista;
  },

  setTotal(state, total) {
    state.total = total;
  },

  setPaginaAtual(state, pagina) {
    state.paginaAtual = pagina;
  },

  setTotalPorPagina(state, totalPorPagina) {
    state.totalPorPagina = totalPorPagina;
  },

  setLoadingState(state, loading) {
    state.loadingState = loading;
  },
};

// Exoprtanto módulo
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
